/**
 * @module experience/shop/accessory-section
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
// CORE
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Router } from '@angular/router';
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { LineDeivceDetails } from '@experience/shop/product-selector/interfaces/product-selector.interface';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { HARDGOODS_CONSTANTS } from '@experience/shop/hardgoods/constants/browse-hardgoods.constants';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { ProductDetailsDefaultModel } from '@experience/shop/shared-browse/models/productDetails-default.model';
import { ProductDetailsService } from '@experience/shop/services/product-details.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UserService } from '@support/shared/services/user.service';

@Component({
  selector: 'tmo-accessory-section',
  templateUrl: './accessory-section.component.html',
  styleUrls: ['../../../../style-sheets/components/online-cart-landing.component.scss'],
})
export class AccessorySectionComponent implements OnInit, OnDestroy {
  /**
   * Stores the content coming from AEM call
   */
  @Input() public configurableContent: ObjectInterface;
  /**
   * stores family name of the selected device
   */
  public familyName: string;
  /**
   * stores Line Setup flag details
   */
  public isLineSetupCompleted: boolean;
  /**
   * Unsubscriber for all subscribtions
   */
  private ngUnsubscribe: Subject<void>;
  /**
   * checks if a product is selected
   */
  @Input() public productExists: boolean;
  /**
   * Checks if byod sim data present for Byod flow
   */
  @Input() public byodSimData: boolean;
  /**
   * fetches product name of device from product offering
   */
  @Input() public productName: string;
  /**
   * checks if page is device landing
   */
  @Input() public deviceLanding: boolean;
  /**
   * checks if page is product selector
   */
  @Input() public productSelectorLanding: boolean;
  /**
   * checks if device is present in cart
   */
  @Input() public isDeviceExistInCart: boolean;
  /**
   * Keeps track of the current lineId in focus details
   */
  public tileIDInFocusLineDetails: LineDeivceDetails;
  /**
   * Keeps track of the current Line details
   */
  @Input() public set focusedTileIdDetails(tileIDInFocusLineDetails: LineDeivceDetails) {
    if (tileIDInFocusLineDetails) {
      this.updateFocusedTileId(tileIDInFocusLineDetails);
    }
  }
  /**
   * checks if there are more than one device in the cart
   */
  @Input() public moreThanOneDevice: boolean;
  /**
   * checks if page is cart landing
   */
  @Input() public isCartLanding: boolean;
  /**
   * boolean flat to check if plane is selected
   */
  public isPlanSelected: boolean;
  /**
   * boolean flat to check if plane is selected
   */
  public noPlanNeeded: boolean;
  /**
   * Flag to identify if is tourist simkit
   */
  public isTouristSimkit: boolean;

  public productDetails: ProductDetailsDefaultModel;
  /**
   * holds value of p10m flag
   */
  public isP10MCustomer: boolean;

  /**
   * Variable to check if channel is web
   */
  // public isWebChannel: boolean;
  /**
   * check if reccomanded accessory section need to display
   * This need to be removed once recommended accessories configuration maintained in the catalog
   */
  public displayAccessoriesSection: boolean;

  constructor(
    private objectService: ObjectService,
    public productDetailsService: ProductDetailsService,
    private stateUtilService: StateStoreUtilsService,
    private router: Router,
    private userService: UserService
  ) {
    this.displayAccessoriesSection = false;
    this.ngUnsubscribe = new Subject<void>();
    this.userService.p10CustomerLoggedIn$
      .pipe(takeUntil(this.ngUnsubscribe.asObservable()))
      .subscribe((data: boolean) => {
        this.isP10MCustomer = data;
      });
  }
  /**
   * Component lifecycle hook to initialize component
   * @returns void
   */
  public ngOnInit(): void {
    // this.isWebChannel = SETTINGS.CHANNEL.toLowerCase() === CHANNELS.WEB;
    const offeringId = this.stateUtilService.getSelectedOfferId();
    this.isTouristSimkit = offeringId === HARDGOODS_CONSTANTS.TOURIST_SIM_KIT;
    this.productDetails = this.stateUtilService.getSelectedDeviceDetails(offeringId);
    if (!this.productName) {
      this.productName = this.objectService.get(this.productDetails, 'familyName') || '';
    }
    this.familyName = this.productName ? this.productName : '';
    this.deviceLanding =
      (this.objectService.get(this.productDetails, 'productType') || '').toLowerCase() !==
      HARDGOODS_CONSTANTS.ACCESSORY;
    if (!this.configurableContent) {
      this.productDetailsService
        .getProductDetailsContent()
        .pipe(takeUntil(this.ngUnsubscribe))
        .subscribe((content: ConfigData) => {
          this.configurableContent = content;
        });
    }
  }
  /**
   * Handles click of Add Accessories link
   */
  public navigateToAccessories(): void {
    this.stateUtilService.setAccessoryDetails(this.productName);
    this.stateUtilService.setAccessoryOfferId(
      this.objectService.get(this.productDetails, 'offerId')
    );
    this.router.navigate([HARDGOODS_CONSTANTS.BROWSE_ACCESSORY_CATEGORY]);
  }
  /**
   * Gets updated line setup data from focused tile
   */
  public updateFocusedTileId(tileIDInFocusLineDetails: LineDeivceDetails): void {
    this.tileIDInFocusLineDetails = tileIDInFocusLineDetails;
    this.isLineSetupCompleted = this.objectService.get(
      tileIDInFocusLineDetails,
      'isLineSetupCompleted'
    );
    this.isPlanSelected = this.objectService.get(tileIDInFocusLineDetails, 'plansFromCart.id');
    this.noPlanNeeded = this.objectService.get(tileIDInFocusLineDetails, 'noPlanNeeded');
  }
  /**
   * Component lifecycle hook to destroy component
   * @returns void
   */
  public ngOnDestroy(): void {}
}
